import { useForm } from '@tanstack/react-form';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { createPendingFolders, validatePendingFolderName, type PendingStorageFolder } from './pendingFolders';

type Props = {
  open: boolean;
  container: string;
  parentPrefix: string;
  onOpenChange: (open: boolean) => void;
  onCreate: (folders: PendingStorageFolder[]) => void;
};

export function StorageNewFolderDialog({ open, container, parentPrefix, onOpenChange, onCreate }: Props) {
  const form = useForm({
    defaultValues: { name: '' },
    onSubmit: ({ value }) => {
      onCreate(createPendingFolders({ container, parentPrefix, name: value.name }));
      void window.electronAPI?.analytics?.track('feature:storage_new_folder', { source: 'toolbar' }).catch(() => undefined);
      onOpenChange(false);
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="storage-new-folder-dialog">
        <DialogHeader>
          {/* U31 / U38: "folder" as in every other view, and the same nested-path rule as the Data Lake view. */}
          <DialogTitle>New folder</DialogTitle>
          <DialogDescription>
            Creates a folder in {parentPrefix ? `${container}/${parentPrefix}` : container}. A path such as 2026/10 also creates its parent folders.
            Blob storage keeps a folder only while it holds a blob, so it shows in this tab until you upload something into it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); void form.handleSubmit(); }} className="space-y-4">
          <form.Field name="name" validators={{
            onChange: ({ value }) => validatePendingFolderName(value),
            onSubmit: ({ value }) => validatePendingFolderName(value),
          }}>
            {(field) => <div className="space-y-2">
              <Label htmlFor="storage-new-folder-name">Folder name</Label>
              <Input id="storage-new-folder-name" spellCheck={false} autoComplete="off" value={field.state.value} onBlur={field.handleBlur} onChange={(event) => field.handleChange(event.target.value)} autoFocus />
              {field.state.meta.errors[0] && <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>}
            </div>}
          </form.Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
              {([canSubmit, isSubmitting]) => <Button type="submit" disabled={!canSubmit || isSubmitting}>Create folder</Button>}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
